
import { Component, Prop, Vue } from "vue-property-decorator";
import { RouteDestination } from "./Passenger/models/RouteWithTrips";
import Trip from "./Passenger/models/Trip";
import moment from "moment";
import { isEtaWithinThreshold } from './RouteDetails.vue';

@Component
export default class RouteDestinationDetails extends Vue {
  @Prop({ default: null }) destination: RouteDestination | null;
  @Prop({ default: [] }) trips: Trip[];
  @Prop({ default: null }) selectedTripId: number | null;
  @Prop({ default: null }) estimatedArrival: Date | null;

  showNestedTrips = false;

  unknownTimeString = '?' as const;

  get hasMultipleTrips() {
    return this.destination.trips.length > 1;
  }

  get firstTripId() {
    return this.destination.trips[0];
  }

  get destinationsTrips() {
    return this.trips.filter(trip => this.destination.trips.includes(trip.id));
  }

  get destinationIsSelected() {
    return this.hasMultipleTrips
      ? !this.showNestedTrips && this.destination.trips.includes(this.selectedTripId)
      : this.selectedTripId === this.firstTripId;
  }

  get formattedTime() {

    const time = this.destination.time ? moment().startOf('day').add(this.destination.time, 'minutes') : null;
    const estimatedTime = this.estimatedArrival ? moment(this.estimatedArrival) : null;
    
//    const diff = estimatedTime && time ? estimatedTime.diff(time, 'minutes') : 0;
    // if (isEtaWithinThreshold(diff)) {
    //   console.log('within threshold');
    //   return time?.format('HH:mm') ?? this.unknownTimeString;
    // }

    const formattedTime = time?.format('HH:mm') ?? this.unknownTimeString;
    if (estimatedTime) { 
      const formattedEstimatedTime = estimatedTime?.format('HH:mm') ?? this.unknownTimeString;
      return `(${formattedTime}) > ${formattedEstimatedTime}`;
    }
    return formattedTime;
  }

  get destinationClasses() {
    return {
      'selected': this.destinationIsSelected,
      'picked-or-dropped': this.isPickedOrDropped(),
      'cancelled' : this.allNoShowOrCancelled(this.firstTripId),
      'no-show': this.isNoShow(this.firstTripId),
    };
  }

  get destinationIcon() {
    if (this.hasMultipleTrips) {
      return this.showNestedTrips ? 'keyboard_arrow_down' : 'chevron_right';
    }
    return 'arrow_right';
  }

  showTime() {
    const destinationDropOffs = this.destination.dropOffs || [];
    if (this.hasMultipleTrips) {
      // If any trip is NOT cancelled or no-show (at dropoff destination)
      return this.destinationsTrips.some(t => !t.cancelledAt && 
        !(t.noShowTime && destinationDropOffs.includes(t.id)));
    }
    const destinationTrip = this.trips[0];
    return !destinationTrip.cancelledAt && 
      !(destinationTrip.noShowTime && destinationDropOffs.includes(destinationTrip.id));
  }

  isCancelled(tripId: number, isNested = this.hasMultipleTrips) {
    if (isNested) {
      return this.destinationsTrips.every(t => !!t.cancelledAt);
    }
    return !!this.trips.find(trip => trip.id === tripId)?.cancelledAt;
  }

  isNoShow(tripId: number, isNested = this.hasMultipleTrips) {
    if (isNested) {
      return this.destinationsTrips.every(t => !!t.noShowTime);
    }
    return !!this.trips.find(trip => trip.id === tripId)?.noShowTime;
  }

  allNoShowOrCancelled(tripId, isNested = this.hasMultipleTrips) {
    if (isNested) {
      return this.destinationsTrips.every(t => !!t.cancelledAt || !!t.noShowTime);
    }
    return !!this.trips.find(trip => trip.id === tripId)?.cancelledAt || !!this.trips.find(trip => trip.id === tripId)?.noShowTime;
  }

  isPickedOrDropped() {
    if (this.hasMultipleTrips) {
      const allHaveEvents = this.destinationsTrips.every(t => t.pickupTime || t.dropoffTime || t.noShowTime || t.cancelledAt || t.passenger.name == null)
      return allHaveEvents && !this.allNoShowOrCancelled(0, this.hasMultipleTrips);
    }
    const trip = this.trips.find(trip => trip.id === this.firstTripId);
    if (this.destination.pickups.includes(this.firstTripId)) {
      return !!trip.pickupTime;
    }
    if (this.destination.dropOffs.includes(this.firstTripId)) {
      return !!trip.dropoffTime;
    }
  }

  getNestedTripClasses(tripId: number) {
    return {
      'hide': !this.showNestedTrips,
      'selected': tripId === this.selectedTripId,
      'cancelled' : this.isCancelled(tripId, false),
    }
  }

  getPassengerName(tripId: number) {
    return this.trips.find(trip => trip.id === tripId).passenger.name ?? this.$t('routeDetails.unknownPassenger');
  }

  destinationClicked() {
    if (this.hasMultipleTrips) {
      this.showNestedTrips = !this.showNestedTrips;
      if (this.showNestedTrips && !this.destination.trips.includes(this.selectedTripId)) {
        this.$emit('selectTrip', this.firstTripId);
      }
    } else {
      this.$emit('selectTrip', this.firstTripId);
    }
  }
}
